import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AREA_UNIT } from 'constants/uiConstants';
import { FARM_KEY_PREFIX, PADDOCK_KEY_PREFIX } from 'constants/routeConstants';
import { IFarm } from 'interfaces/IFarm';
import { IPaddock } from 'interfaces/IPaddock';
import { convertToHectares } from 'utils/unitConversion';
import { RootState } from '../redux/Store';
import { useFetchOrganisations } from './useFetchOrganisations';
import { useVisibleOrgData } from 'hooks/useVisibleOrgData';

export interface SiteAutofill {
  /** "Organisation, Farm, Paddock" — as much of it as the node resolves to. */
  siteLabel: string;
  /** Paddock area in ha, or null when the node isn't a paddock. */
  areaHa: number | null;
}

/**
 * Tree node key for the most specific farm/paddock selection, or null at
 * organisation scope. Only farm and paddock keys are produced, so the key is
 * safe to hand to useSiteAutofill from anywhere (e.g. a URL param).
 */
export const buildSiteNodeKey = (
  farmId: string | null,
  paddockId: string | null
): string | null => {
  if (paddockId) return `${PADDOCK_KEY_PREFIX}${paddockId}`;
  if (farmId) return `${FARM_KEY_PREFIX}${farmId}`;
  return null;
};

/**
 * Resolves a farm or paddock node key into autofill values for forms that
 * describe a site (OP-2383 — LimeMate). The organisation is always the
 * active one. Reads the organisations and farms query caches the Farms page
 * already holds, so it adds no requests there. A key that doesn't match
 * anything in the active organisation yields just the organisation name.
 */
export const useSiteAutofill = (nodeKey: string | null): SiteAutofill => {
  const { organisationId } = useSelector(
    (state: RootState) => state.activeOrganisationReducer
  );
  const { data: organisations = [] } = useFetchOrganisations();
  const { farms } = useVisibleOrgData(organisationId);

  return useMemo(() => {
    const org = organisations.find((o) => o.id === organisationId);

    let farm: IFarm | null = null;
    let paddock: IPaddock | null = null;
    if (nodeKey?.startsWith(PADDOCK_KEY_PREFIX)) {
      const paddockId = nodeKey.slice(PADDOCK_KEY_PREFIX.length);
      for (const f of farms) {
        const match = f.paddocks.find((p) => p.paddockId === paddockId);
        if (match) {
          farm = f;
          paddock = match;
          break;
        }
      }
    } else if (nodeKey?.startsWith(FARM_KEY_PREFIX)) {
      const farmId = nodeKey.slice(FARM_KEY_PREFIX.length);
      farm = farms.find((f) => f.id === farmId) ?? null;
    }

    let areaHa: number | null = null;
    if (paddock) {
      // Arable area first, as the workspace header shows it.
      const area =
        paddock.arableArea > 0 ? paddock.arableArea : paddock.paddockArea;
      if (area > 0) {
        const ha = convertToHectares(
          area,
          paddock.areaUnit ?? { unit: AREA_UNIT, globalId: null },
          []
        );
        areaHa = ha != null ? Number(ha.toFixed(2)) : null;
      }
    }

    return {
      siteLabel: [org?.orgName, farm?.farmName, paddock?.paddockName]
        .filter(Boolean)
        .join(', '),
      areaHa,
    };
  }, [organisations, organisationId, farms, nodeKey]);
};
